"use client";

import {
  MouseEvent,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import Link from "next/link";
import Image from "next/image";
import { LuAlignJustify, LuX } from "react-icons/lu";
import Logo from "../../public/Logo.png";
import { HomeSubscribe } from "./HomeSubscribe";
import styles from "./Header.module.css";

type Menu = "none" | "nav" | "subscribe";

export const Header = () => {
  const [menu, setMenu] = useState<Menu>("none");
  const dialogRef = useRef<HTMLDialogElement>(null);
  const sheetRef = useRef<HTMLDivElement>(null);
  const subscribeButtonRef = useRef<HTMLButtonElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const subscribeTitleId = useId();
  const navMenuId = useId();

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (menu === "subscribe") {
      if (!dialog.open) {
        dialog.showModal();
      }
      window.requestAnimationFrame(() => {
        const emailField = dialog.querySelector<HTMLInputElement>(
          'input[type="email"]',
        );
        emailField?.focus();
      });
      return;
    }

    if (dialog.open) {
      dialog.close();
    }
  }, [menu]);

  useEffect(() => {
    if (menu !== "subscribe") return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [menu]);

  useEffect(() => {
    if (menu !== "nav") return;

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setMenu("none");
      }
    }

    window.addEventListener("keydown", onKeyDown);
    window.requestAnimationFrame(() => {
      const first = sheetRef.current?.querySelector<HTMLElement>("a, button");
      first?.focus();
    });

    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [menu]);

  useEffect(() => {
    function onResize() {
      if (window.innerWidth > 768) {
        setMenu((current) => (current === "nav" ? "none" : current));
      }
    }

    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  function openSubscribe() {
    setMenu("subscribe");
  }

  function restoreChromeFocus() {
    if (window.matchMedia("(max-width: 768px)").matches) {
      menuButtonRef.current?.focus();
      return;
    }
    subscribeButtonRef.current?.focus();
  }

  function closeSubscribe() {
    setMenu("none");
    window.requestAnimationFrame(restoreChromeFocus);
  }

  function onDialogClose() {
    setMenu((current) => (current === "subscribe" ? "none" : current));
    restoreChromeFocus();
  }

  function onDialogClick(event: MouseEvent<HTMLDialogElement>) {
    if (event.target === event.currentTarget) {
      event.currentTarget.close();
    }
  }

  function toggleNav() {
    setMenu((current) => (current === "nav" ? "none" : "nav"));
  }

  return (
    <header className={styles.siteHeader}>
      <div className={styles.inner}>
        <Link href="/" className={styles.brand}>
          <Image
            src={Logo}
            alt="Home"
            width={32}
            height={32}
            className={styles.logo}
            priority
          />
          <span className={styles.brandName}>Nikola Knezevic</span>
        </Link>
        <nav className={styles.nav} aria-label="Primary">
          <Link href="/blogs" className={`${styles.navLink} ${styles.desktopOnly}`}>
            Blogs
          </Link>
          <Link
            href="/sponsorship"
            className={`${styles.navLink} ${styles.desktopOnly}`}
          >
            Sponsorship
          </Link>
          <button
            ref={subscribeButtonRef}
            type="button"
            className={`${styles.subscribe} ${styles.desktopOnly}`}
            onClick={openSubscribe}
          >
            Subscribe
          </button>
        </nav>
        <div className={styles.menuWrap}>
          <button
            ref={menuButtonRef}
            type="button"
            className={styles.menuButton}
            aria-label={menu === "nav" ? "Close menu" : "Open menu"}
            aria-expanded={menu === "nav"}
            aria-controls={navMenuId}
            onClick={toggleNav}
          >
            {menu === "nav" ? (
              <LuX aria-hidden="true" />
            ) : (
              <LuAlignJustify aria-hidden="true" />
            )}
          </button>
          {menu === "nav" ? (
            <>
              <button
                type="button"
                className={styles.sheetScrim}
                aria-label="Close menu"
                onClick={() => setMenu("none")}
              />
              <div
                ref={sheetRef}
                id={navMenuId}
                className={styles.sheet}
                role="navigation"
                aria-label="Mobile"
              >
                <Link
                  href="/blogs"
                  className={styles.sheetLink}
                  onClick={() => setMenu("none")}
                >
                  Blogs
                </Link>
                <Link
                  href="/sponsorship"
                  className={styles.sheetLink}
                  onClick={() => setMenu("none")}
                >
                  Sponsorship
                </Link>
                <hr className={styles.sheetRule} />
                <button
                  type="button"
                  className={styles.sheetSubscribe}
                  onClick={openSubscribe}
                >
                  Subscribe
                </button>
              </div>
            </>
          ) : null}
        </div>
      </div>

      <dialog
        ref={dialogRef}
        className={styles.dialog}
        aria-labelledby={subscribeTitleId}
        onClose={onDialogClose}
        onClick={onDialogClick}
      >
        <div className={styles.dialogPanel}>
          <button
            type="button"
            className={styles.dialogClose}
            aria-label="Close"
            onClick={closeSubscribe}
          >
            <LuX aria-hidden="true" />
          </button>
          <p className={styles.dialogKicker}>Newsletter</p>
          <h2 id={subscribeTitleId} className={styles.dialogTitle}>
            Get the Thursday issue.
          </h2>
          <p className={styles.dialogCopy}>
            One focused C# tutorial. Free, and easy to leave.
          </p>
          <HomeSubscribe id="header-subscribe" variant="light" stacked />
          <ul className={styles.dialogProof}>
            <li>4,000+ engineers</li>
            <li>100+ tutorials</li>
          </ul>
        </div>
      </dialog>
    </header>
  );
};

export default Header;
